<template>
<!-- Hero Section -->
    <section id="home" class="hero">
        <div class="hero-container">
            <div class="hero-left">
                <div class="label">Entrepreneur • Innovator • Problem Solver</div>
                <h1><strong>Building</strong> Businesses<br>That <strong>Matter</strong></h1>
                <p class="subtitle">Co-Founder | AI-Powered Solutions for Real-World Challenges</p>
                <p class="tagline">Transforming industries through technology, networks, and strategic innovation</p>
                <div class="cta-buttons">
                    <a href="#ventures" class="btn btn-primary">View Ventures</a>
                    <a href="#contact" class="btn btn-secondary">Get in Touch</a>
                </div>
            </div>
            <div class="hero-right">
                <div class="hero-image">
                    <img src="../assets/images/profile-photo.jpeg" alt="Vipandeep Singh" class="hero-image">
                </div>
            </div>
        </div>
    </section>

    <!-- About Section -->
    <section id="about" class="about">
        <div class="container">
            <div class="section-header">
                <div class="section-label">Who I Am</div>
                <h2 class="section-title">An Entrepreneur Who Sees <strong>Opportunities</strong><br>Where Others See Obstacles</h2>
            </div>
            <div class="about-content">
                <p>I'm Vipandeep Singh—with ventures spanning real estate technology and business automation, I'm driven by a simple belief: the best businesses solve real problems for real people. My approach combines deep business acumen, technical innovation, and an extensive network built over years of connecting with diverse industries.</p>
                
                <p>As co-founder of ClientsPreferred Properties and Bizzento, I'm reshaping how businesses operate—from revolutionizing real estate marketing with AI agents to empowering small businesses with intelligent automation. And I'm just getting started.</p>
                
                <p>My unconventional journey—from fitness training and real estate to technology entrepreneurship—has given me unique insights into what businesses actually need, not just what technology can do.</p>
            </div>
        </div>
    </section>

    <!-- Ventures Section -->
    <section id="ventures" class="ventures">
        <div class="container">
            <div class="section-header">
                <div class="section-label">What I Build</div>
                <h2 class="section-title"><strong>Current Ventures</strong></h2>
                <p class="section-description">Building solutions that solve real business problems</p>
            </div>
            
            <div class="ventures-grid">
                <div class="venture-card">
                    <div class="role">Co-Founder</div>
                    <h3><a href="https://bizzento.com" target="_blank">Bizzento</a></h3>
                    <div class="subtitle">AI-Powered Business Automation</div>
                    <p>Building intelligent automation solutions specifically designed for the small business market. Bizzento isn't just another AI tool—it's a business partner that understands local context, workflows, and challenges.</p>
                    <p>Using my extensive network and deep understanding of small business operations, I'm developing custom solutions for high-profile clients who need more than off-the-shelf software.</p>
                </div>

                <div class="venture-card">
                    <div class="role">Founder</div>
                    <h3><a href="https://clientspreferred.com" target="_blank">Clients Preferred<br>Properties</a></h3>
                    <div class="subtitle">Real Estate Innovation</div>
                    <p>Developing Airea—an AI Real Estate Agent that's transforming property marketing and client engagement. Drawing from my experience as a real estate agent and network builder, we're solving real pain points in the industry.</p>
                    <p>This venture combines my understanding of relationship-driven business with cutting-edge AI to create something truly differentiated.</p>
                </div>

                <div class="venture-card">
                    <div class="role">In Development</div>
                    <h3>Next Ventures</h3>
                    <div class="subtitle">Building the Future</div>
                    <p>Currently exploring and developing additional business opportunities across various sectors. My goal: become a serial entrepreneur who builds sustainable, impactful businesses.</p>
                    <p>Every new venture starts with identifying real problems and architecting solutions that work in the real world.</p>
                </div>
            </div>
        </div>
    </section>

    <!-- Strengths Section -->
    <section id="strengths" class="strengths">
        <div class="container">
            <div class="section-header">
                <div class="section-label">My Edge</div>
                <h2 class="section-title">What Sets Me <strong>Apart</strong></h2>
            </div>
            
            <div class="strengths-grid">
                <div class="strength-item">
                    <div class="number">01</div>
                    <h3>Business Problem Identification</h3>
                    <p>I don't build technology for technology's sake. I identify real business problems through my extensive network, understand the pain points deeply, and then architect solutions that actually work in the real world.</p>
                </div>

                <div class="strength-item">
                    <div class="number">02</div>
                    <h3>Network & Relationships</h3>
                    <p>Years of working in real estate, fitness, and various industries have given me a diverse, valuable network. I leverage these connections to understand market needs and create opportunities.</p>
                </div>

                <div class="strength-item">
                    <div class="number">03</div>
                    <h3>Execution Capability</h3>
                    <p>Unlike pure "idea people," I have the technical expertise to actually build what I envision. My 9+ years in software engineering and test automation give me the skills to architect, develop, and scale solutions.</p>
                </div>

                <div class="strength-item">
                    <div class="number">04</div>
                    <h3>Market Understanding</h3>
                    <p>From working with small business owners to corporate clients, from real estate to e-commerce—I've seen how different businesses operate and what they truly need to succeed.</p>
                </div>
            </div>
        </div>
    </section>

    <!-- Journey Section -->
    <section id="journey" class="journey">
        <div class="container">
            <div class="section-header">
                <div class="section-label">My Path</div>
                <h2 class="section-title">The Journey: From Discipline to <strong>Innovation</strong></h2>
                <p class="section-description">My path to entrepreneurship wasn't traditional—it was transformative</p>
            </div>
            
            <div class="timeline">
                <div class="timeline-item">
                    <div class="timeline-date">Phase One</div>
                    <div class="timeline-content">
                        <h3>The Early Years: Building Foundation</h3>
                        <p>During my B.Tech, I was deep into bodybuilding, powerlifting, and working as a bouncer and call center agent. This taught me discipline, resilience, and how to work with people from all walks of life.</p>
                    </div>
                </div>

                <div class="timeline-item">
                    <div class="timeline-date">Phase Two</div>
                    <div class="timeline-content">
                        <h3>The Hybrid Phase: Multiple Worlds</h3>
                        <p>While building my IT career, I simultaneously worked as a personal fitness trainer, sold gym supplements, and continued bouncer work. I wasn't just collecting paychecks—I was building business sense and understanding customer needs across industries.</p>
                    </div>
                </div>

                <div class="timeline-item">
                    <div class="timeline-date">Phase Three</div>
                    <div class="timeline-content">
                        <h3>The Real Estate Education</h3>
                        <p>Transitioning into real estate as an agent wasn't a detour—it was business school. I learned negotiation, network building, market dynamics, and most importantly, how to identify what people really want versus what they say they want.</p>
                    </div>
                </div>

                <div class="timeline-item">
                    <div class="timeline-date">Phase Four</div>
                    <div class="timeline-content">
                        <h3>The Tech Foundation</h3>
                        <p>My career at QASource and Basware gave me something invaluable: the ability to build scalable, quality-driven technology. I became a Principal Software Engineer, developed AI-powered tools, and mentored teams—but more importantly, I learned how to architect solutions that work.</p>
                    </div>
                </div>

                <div class="timeline-item">
                    <div class="timeline-date">Phase Five</div>
                    <div class="timeline-content">
                        <h3>The Entrepreneurial Leap</h3>
                        <p>All these experiences converged into one realization: I could identify business problems, build the technical solutions, and leverage my network to create real value. That's when ClientsPreferredProperties and Pabu were born.</p>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- Philosophy Section -->
    <section id="philosophy" class="philosophy">
        <div class="container">
            <div class="section-header">
                <div class="section-label">Core Values</div>
                <h2 class="section-title"><strong>Philosophy</strong></h2>
                <p class="section-description">The principles that guide everything I build</p>
            </div>
            
            <div class="philosophy-grid">
                <div class="philosophy-item">
                    <div class="number">01</div>
                    <h3>Build With Purpose</h3>
                    <p>Every venture I start solves a real problem for real people. No vanity projects, no technology for technology's sake.</p>
                </div>

                <div class="philosophy-item">
                    <div class="number">02</div>
                    <h3>Network is Net Worth</h3>
                    <p>The strongest businesses are built on relationships. I invest heavily in understanding people, building trust, and creating genuine connections.</p>
                </div>

                <div class="philosophy-item">
                    <div class="number">03</div>
                    <h3>Embrace the Unconventional</h3>
                    <p>My non-linear path is my competitive advantage. I see opportunities and solutions others miss because I've lived in multiple worlds.</p>
                </div>

                <div class="philosophy-item">
                    <div class="number">04</div>
                    <h3>Execute Relentlessly</h3>
                    <p>Ideas are abundant. Execution is rare. I bridge the gap between vision and reality.</p>
                </div>
            </div>
        </div>
    </section>

    <!-- CTA Section -->
    <section id="contact" class="cta-section">
        <div class="container">
            <div class="section-label">Connect</div>
            <h2>Let's Build <strong>Together</strong></h2>
            <p>Whether you're looking for a co-founder, a strategic partner, or someone who can identify and solve complex business problems—let's talk.</p>
            <p>I'm always exploring new opportunities, building new ventures, and connecting with people who think differently.</p>
            
            <ul>
                <li>Interested in collaborating?</li>
                <li>Have a business problem that needs solving?</li>
                <li>Looking for strategic insights in AI, automation, or real estate tech?</li>
            </ul>
            
            <a href="mailto:vipandeeps595@gmail.com" class="btn btn-primary">Get In Touch</a>
        </div>
    </section>
</template>

<script>
export default {
  name: "HeroSection",
};
</script>

<style scoped>

</style>
